import { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';

import { api } from '../api.js';
import { toast } from './Toasts.jsx';
import { relativeDay } from './ScheduledList.jsx';
import ApplicationPicker from './ApplicationPicker.jsx';

// T395 — the things he decides to do.
//
// Everything else on this page is DERIVED: a stage change a reply proposed, a
// message the rules could not place, an interview lifted off a calendar
// invite. All reactive. "Ring Kasper on Thursday" is not derivable from
// anything the platform can see, so until now there was nowhere to put it.
//
// Rendered in the same section as the scheduled events rather than in a list
// of its own. Two lists of things-to-do on one page is the fragmentation this
// whole page was built to end.

function dueClass(t) {
  if (t.doneAt) return 'text-slate-600';
  if (!t.dueAt) return 'text-slate-500';
  return new Date(t.dueAt) < new Date() ? 'text-amber-400' : 'text-slate-400';
}

function AddTask({ candidatures, onAdded }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [recommendationId, setRecommendationId] = useState('');
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const r = await api.post('/tasks', {
        title: title.trim(),
        dueAt: dueAt || null,
        recommendationId: recommendationId || null,
      });
      setTitle('');
      setDueAt('');
      setRecommendationId('');
      setPicked(null);
      setOpen(false);
      onAdded(r.task);
      toast('Task added.', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || 'Could not add that.', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-slate-700 px-2.5 py-1 text-[11px] text-slate-300 transition hover:border-brand-600 hover:text-brand-400"
      >
        + Add a task
      </button>
    );
  }

  return (
    <div className="space-y-1.5 rounded-md border border-slate-800 bg-slate-950/40 p-2">
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') setOpen(false);
        }}
        placeholder="What needs doing?"
        aria-label="Task"
        className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100 focus:border-brand-500 focus:outline-none"
      />
      <div className="flex flex-wrap items-center gap-2">
        {/* No default date. A due date he did not choose is a deadline he did
            not agree to, and an overdue badge he never earned. */}
        <input
          type="date"
          value={dueAt}
          onChange={(e) => setDueAt(e.target.value)}
          aria-label="Due date"
          className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-100 focus:border-brand-500 focus:outline-none"
        />
        {/* T437 — the typed picker, not a native <select> over 106 rows.
            T366 built this for exactly this problem on the mail card: a
            dropdown of a hundred applications has no way in except
            scrolling, and the product should not have two answers to the
            same question. `picked` is local so the control can show what
            was chosen; clearing it is how "no application" is expressed. */}
        <div className="min-w-0 flex-1">
          {picked ? (
            <button
              type="button"
              onClick={() => {
                setPicked(null);
                setRecommendationId('');
              }}
              className="w-full truncate rounded border border-slate-700 bg-slate-900 px-2 py-1 text-left text-xs text-slate-200 hover:border-brand-600"
              title="Clear"
            >
              {picked.company} — {picked.title} <span className="text-slate-500">✕</span>
            </button>
          ) : (
            <ApplicationPicker
              candidatures={candidatures}
              // ApplicationPicker hands back the ID, not the row — the
              // first version of this took an object and would have
              // rendered "undefined — undefined" the moment he picked one.
              onPick={(id) => {
                setRecommendationId(id);
                setPicked(candidatures.find((c) => c.id === id) ?? null);
              }}
            />
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={busy || !title.trim()}
          onClick={submit}
          className="rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50"
        >
          Add
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[11px] text-slate-500 underline decoration-dotted hover:text-slate-300"
        >
          cancel
        </button>
      </div>
    </div>
  );
}

function TaskRow({ t, onChanged, onRemoved }) {
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/tasks/${t.id}`, { done: !t.doneAt });
      onChanged(r.task);
    } catch (e) {
      toast(e.body?.message || 'Could not update that.', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/tasks/${t.id}`);
      onRemoved(t.id);
      toast('Removed.', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || 'Could not remove that.', { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const overdue = !t.doneAt && t.dueAt && new Date(t.dueAt) < new Date();

  return (
    <li
      className={
        'rounded-md border px-3 py-2 ' +
        (overdue ? 'border-amber-800/60 bg-amber-950/20' : 'border-slate-800 bg-slate-950/40')
      }
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={Boolean(t.doneAt)}
          disabled={busy}
          onChange={toggle}
          aria-label={t.doneAt ? `Reopen ${t.title}` : `Complete ${t.title}`}
          className="mt-0.5 shrink-0"
        />
        <span className="min-w-0 flex-1">
          <span className={'block text-sm ' + (t.doneAt ? 'text-slate-500 line-through' : 'text-slate-200')}>
            {t.title}
          </span>
          <span className="mt-0.5 block text-[11px]">
            <span className={dueClass(t)}>{t.dueAt ? relativeDay(t.dueAt) : 'no date'}</span>
            {t.job && <span className="text-slate-500"> · {t.job.company} — {t.job.title}</span>}
          </span>
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={remove}
          aria-label={`Remove ${t.title}`}
          className="shrink-0 text-[11px] text-slate-600 transition hover:text-red-400 disabled:opacity-50"
        >
          remove
        </button>
      </div>
    </li>
  );
}

const OwnTasks = forwardRef(function OwnTasks({ onCount }, ref) {
  const [tasks, setTasks] = useState(null);
  const [done, setDone] = useState([]);
  const [candidatures, setCandidatures] = useState([]);
  // T434 — a section that cannot load must not read as a section with
  // nothing in it. Same rule StageProposals got in T387.
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/tasks');
      setTasks(r.tasks ?? []);
      setDone(r.done ?? []);
      // T416 — from the tasks endpoint, not from the mail queue. This list
      // used to come from `GET /emails?unmatched=1&limit=1`, so the task
      // form depended on mail loading, and the request walked 848 queue
      // rows to build a field nobody here reads.
      setCandidatures(r.candidatures ?? []);
      setFailed(false);
      onCount?.(r.tasks?.length ?? 0);
    } catch {
      setFailed(true);
      setTasks([]);
    }
  }, [onCount]);

  useImperativeHandle(ref, () => ({ reload: load }), [load]);

  useEffect(() => {
    load();
  }, [load]);

  const changed = (t) => {
    load();
    return t;
  };
  const removed = () => load();

  if (!tasks) return null;

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium text-brand-500">Your tasks</h2>
        <AddTask candidatures={candidatures} onAdded={() => load()} />
      </div>
      {failed ? (
        <p className="max-w-prose text-xs text-slate-500">
          Could not load your tasks just now — this is not the same as having none.
        </p>
      ) : tasks.length === 0 && done.length === 0 ? (
        <p className="max-w-prose text-xs text-slate-500">
          Nothing you have written down. Everything else on this page comes from your mail — this is
          the part that does not.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {tasks.map((t) => (
            <TaskRow key={t.id} t={t} onChanged={changed} onRemoved={removed} />
          ))}
          {done.map((t) => (
            <TaskRow key={t.id} t={t} onChanged={changed} onRemoved={removed} />
          ))}
        </ul>
      )}
    </section>
  );
});

export default OwnTasks;
